// Central data store for specialties. Easy to edit/add new entries.
import {
    HeartPulse,
    Brain,
    Bone,
    Activity,
    Microscope,
    Scissors,
    Baby,
    Eye,
    Stethoscope,
    Pill,
} from "lucide-react";

export const SPECIALTIES = [
    {
        slug: "cardiology",
        name: "Cardiology & Cardiac Surgery",
        shortName: "Cardiac Surgery",
        icon: HeartPulse,
        tagline: "World-class heart care, in expert hands.",
        overview:
            "Our cardiology network combines internationally trained surgeons with state-of-the-art cath labs and hybrid operating theatres, delivering outcomes that rival any leading global centre — at a fraction of the cost.",
        image: "https://res.cloudinary.com/deq3rnrha/image/upload/v1776764634/Cardiac_Surgery_ms5pft.jpg",
        conditions: [
            "Coronary Artery Disease",
            "Heart Valve Disease",
            "Heart Failure",
            "Arrhythmias",
            "Congenital Heart Defects",
            "Aortic Aneurysm",
        ],
        categories: [
            "Diagnostic Cardiology",
            "Interventional Cardiology",
            "Cardiac Surgery",
            "Electrophysiology",
        ],
        treatments: ["cabg", "angioplasty", "valve-replacement", "tavr", "pacemaker"],
        doctors: ["naresh-trehan"],
        faqs: [
            {
                q: "How soon can I get a cardiac consultation in India?",
                a: "Most international patients receive a written opinion from a senior cardiologist within 24–48 hours of sharing their reports with us.",
            },
            {
                q: "Is bypass surgery in India safe for foreign patients?",
                a: "Yes. India performs over 250,000 cardiac procedures annually with success rates of 96–99%, comparable to the US and UK.",
            },
            {
                q: "How long should I plan to stay in India?",
                a: "Plan for 3 weeks total — pre-op evaluation, surgery, hospital stay (7–10 days) and one outpatient review before flying home.",
            },
        ],
    },
    {
        slug: "neurology",
        name: "Neurology & Neurosurgery",
        shortName: "Neurosurgery",
        icon: Brain,
        tagline: "Precision neuroscience for life-changing outcomes.",
        overview:
            "From robotic spine surgery to awake craniotomies for tumour removal, our neurosurgery partners offer the full spectrum of brain and spine care with multi-disciplinary tumour boards reviewing every complex case.",
        image: "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=1600&q=80",
        conditions: [
            "Brain Tumours",
            "Spinal Disorders",
            "Stroke",
            "Epilepsy",
            "Parkinson's Disease",
            "Trigeminal Neuralgia",
        ],
        categories: [
            "Brain Surgery",
            "Spine Surgery",
            "Functional Neurosurgery",
            "Neuro-Oncology",
        ],
        treatments: ["spine-surgery", "brain-tumour", "dbs", "spine-tumour"],
        doctors: ["sandeep-vaishya"],
        faqs: [
            {
                q: "Do you offer minimally invasive spine surgery?",
                a: "Yes. Most of our partner hospitals perform endoscopic and robotic-assisted spine surgery with same-day mobilisation.",
            },
            {
                q: "Can complex brain tumours be operated safely in India?",
                a: "India's leading neurosurgery centres have intra-operative MRI, awake craniotomy capabilities and one of the largest brain tumour case loads globally.",
            },
        ],
    },
    {
        slug: "orthopedics",
        name: "Orthopaedics & Joint Replacement",
        shortName: "Orthopaedics",
        icon: Bone,
        tagline: "Move freely again — with India's most experienced joint surgeons.",
        overview:
            "Computer-navigated knee replacements, robotic hip surgery, advanced sports medicine and complex spine reconstruction — all backed by dedicated rehab teams.",
        image: "https://res.cloudinary.com/deq3rnrha/image/upload/v1778868881/ChatGPT_Image_May_15_2026_11_33_57_PM_odkcte.png",
        conditions: [
            "Osteoarthritis",
            "Rheumatoid Arthritis",
            "Avascular Necrosis",
            "Sports Injuries",
            "Spinal Disc Disease",
            "Fractures",
        ],
        categories: [
            "Joint Replacement",
            "Sports Medicine",
            "Spine Surgery",
            "Trauma & Reconstruction",
        ],
        treatments: ["knee-replacement", "hip-replacement", "spine-surgery", "acl-reconstruction"],
        doctors: ["hemant-sharma"],
        faqs: [
            {
                q: "How long after knee replacement can I fly home?",
                a: "Most patients fly home 10–14 days after surgery, once cleared by the orthopaedic team and travel medicine consult.",
            },
            {
                q: "Will I need physiotherapy?",
                a: "Yes, our coordinator arranges in-hospital physiotherapy and a home-exercise programme you can continue back in your country.",
            },
        ],
    },
    {
        slug: "oncology",
        name: "Oncology & Cancer Care",
        shortName: "Oncology",
        icon: Microscope,
        tagline: "Comprehensive cancer care — backed by world-class technology.",
        overview:
            "From precision radiotherapy (CyberKnife, TrueBeam) to robotic surgery and bone marrow transplant, our cancer centres deliver protocol-driven, multi-disciplinary care for adult and paediatric oncology.",
        image: "https://res.cloudinary.com/deq3rnrha/image/upload/v1779006406/Oncology_Cancer_eywjx1.png",
        conditions: [
            "Breast Cancer",
            "Lung Cancer",
            "Colorectal Cancer",
            "Blood Cancers (Leukaemia, Lymphoma)",
            "Head & Neck Cancers",
            "Paediatric Cancers",
        ],
        categories: [
            "Medical Oncology",
            "Surgical Oncology",
            "Radiation Oncology",
            "Haematology & BMT",
        ],
        treatments: ["chemotherapy", "radiation-therapy", "robotic-cancer-surgery", "bmt"],
        doctors: ["rahul-bhargava", "vinod-raina", "vedant-kabra", "amal-roy-chudhury"],
        faqs: [
            {
                q: "Will my treatment protocol match international guidelines?",
                a: "Yes. Our partner oncology centres follow NCCN, ESMO and protocol-specific guidelines and present every case in a tumour board.",
            },
            {
                q: "Can I get a second opinion before traveling?",
                a: "Absolutely — we offer free written second opinions from senior oncologists within 48 hours of receiving your reports.",
            },
        ],
    },
    {
        slug: "gastroenterology",
        name: "Gastroenterology, GI & Liver Surgery",
        shortName: "GI & Liver Surgery",
        icon: Activity,
        tagline: "Advanced GI, HPB and transplant surgery under one roof.",
        overview:
            "India is one of the world's leading destinations for liver transplantation and complex hepato-biliary surgery, with high-volume centres performing 1000+ liver transplants a year.",
        image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1600&q=80",
        conditions: [
            "End-Stage Liver Disease",
            "Gallstones",
            "Pancreatic Disorders",
            "Inflammatory Bowel Disease",
            "GI Cancers",
            "Hernias",
        ],
        categories: [
            "Hepatology",
            "Liver Transplant",
            "Bariatric Surgery",
            "Endoscopy & Colonoscopy",
        ],
        treatments: ["liver-transplant", "bariatric-surgery"],
        doctors: ["vivek-vij"],
        faqs: [
            {
                q: "Is living-donor liver transplant available?",
                a: "Yes, with comprehensive evaluation, ethics clearance and one of the world's largest experience in adult and paediatric LDLT.",
            },
            {
                q: "What is the success rate for liver transplant in India?",
                a: "1-year survival rates exceed 90% at high-volume Indian centres, on par with leading US and European programmes.",
            },
        ],
    },
    {
        slug: "transplants",
        name: "Organ Transplants",
        shortName: "Organ Transplants",
        icon: Scissors,
        tagline: "Comprehensive transplant care — liver, kidney, heart, bone marrow.",
        overview:
            "We coordinate evaluation, donor workup, ethics clearance and transplant logistics across India's most experienced transplant programmes.",
        image: "https://images.unsplash.com/photo-1582719471384-894fbb16e074?w=1600&q=80",
        conditions: [
            "End-Stage Liver Disease",
            "End-Stage Renal Disease",
            "Severe Cardiomyopathy",
            "Blood Cancers",
        ],
        categories: [
            "Liver Transplant",
            "Kidney Transplant",
            "Heart Transplant",
            "Bone Marrow Transplant",
        ],
        treatments: ["liver-transplant", "bmt"],
        doctors: ["vivek-vij", "rahul-bhargava"],
        faqs: [],
    },
    {
        slug: "fertility",
        name: "Fertility & IVF",
        shortName: "Fertility & IVF",
        icon: Baby,
        tagline: "Modern reproductive medicine with personalised care.",
        overview:
            "Comprehensive IVF, ICSI, IUI, donor programmes and fertility preservation — with embryology labs of international standard.",
        image: "https://images.unsplash.com/photo-1584515933487-779824d29309?w=1600&q=80",
        conditions: ["Infertility", "PCOS", "Endometriosis", "Recurrent Pregnancy Loss"],
        categories: ["IVF / ICSI", "Donor Programmes", "Fertility Preservation"],
        treatments: [],
        doctors: ["rachita-munjal"],
        faqs: [],
    },
    {
        slug: "ophthalmology",
        name: "Ophthalmology",
        shortName: "Ophthalmology",
        icon: Eye,
        tagline: "Advanced eye care — from refractive surgery to retinal procedures.",
        overview:
            "LASIK, SMILE, premium IOL cataract surgery, glaucoma management and complex retina care — performed by globally trained ophthalmologists.",
        image: "https://images.unsplash.com/photo-1551601651-bc60f254d532?w=1600&q=80",
        conditions: ["Cataract", "Glaucoma", "Refractive Errors", "Retinal Disorders"],
        categories: ["Refractive Surgery", "Cataract", "Retina", "Cornea & Glaucoma"],
        treatments: [],
        doctors: [],
        faqs: [],
    },
    {
        slug: "bariatric",
        name: "Bariatric Surgery",
        shortName: "Bariatric Surgery",
        icon: Stethoscope,
        tagline: "Sustainable weight loss surgery with lifelong support.",
        overview:
            "Sleeve gastrectomy, gastric bypass and revision bariatric surgery — supported by nutrition, endocrinology and psychological counselling.",
        image: "https://images.unsplash.com/photo-1559757175-7cb036bd4d31?w=1600&q=80",
        conditions: ["Morbid Obesity", "Type-2 Diabetes", "Sleep Apnoea"],
        categories: ["Sleeve Gastrectomy", "Gastric Bypass", "Revision Surgery"],
        treatments: ["bariatric-surgery"],
        doctors: [],
        faqs: [],
    },
    {
        slug: "cosmetic",
        name: "Cosmetic & Plastic Surgery",
        shortName: "Cosmetic & Plastic Surgery",
        icon: Pill,
        tagline: "Aesthetic excellence with safety-first standards.",
        overview:
            "Facial aesthetics, body contouring, breast surgery, hair restoration and reconstructive procedures by board-certified plastic surgeons.",
        image: "https://images.unsplash.com/photo-1571442463800-1337d7af9d2f?w=1600&q=80",
        conditions: [],
        categories: ["Aesthetic Surgery", "Reconstructive Surgery", "Hair Transplant"],
        treatments: [],
        doctors: ["vipul-nanda"],
        faqs: [],
    },
];

export const getSpecialty = (slug) =>
    SPECIALTIES.find((s) => s.slug === slug);
